import React from 'react';
import { SimpleTooltip } from '@tanium/react-tooltip';
import { parseHostnames } from '../utilities/instanceHelper';

const EMPTY_CELL = '--';

interface HostnamesCellProps {
  /** The deployment's hostnames as the service sends them: one comma-separated string. */
  hostnames?: string;
}

/**
 * A deployment's hostnames in a table cell. A multi-server deployment would otherwise blow the
 * column out, so only the first hostname is shown with a trailing ellipsis, and the full list is a
 * hover away. The tooltip is attached only when there is something more to reveal.
 */
export const HostnamesCell: React.VFC<HostnamesCellProps> = ({ hostnames }) => {
  const parsed = parseHostnames(hostnames);
  if (parsed.length === 0) {
    return <>{EMPTY_CELL}</>;
  }
  const [first, ...rest] = parsed;
  if (rest.length === 0) {
    return <>{first}</>;
  }
  return (
    <SimpleTooltip
      content={
        <div>
          {parsed.map((hostname) => (
            <div key={hostname}>{hostname}</div>
          ))}
        </div>
      }
    >
      {({ ref }: { ref: React.Ref<HTMLSpanElement> }) => (
        // The full list stays in the accessible name so screen readers and tests are not limited to
        // what the hover reveals.
        <span ref={ref} aria-label={parsed.join(', ')}>{`${first} …`}</span>
      )}
    </SimpleTooltip>
  );
};
